import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  addR,
  divR,
  equalsR,
  formatFraction,
  formatPolynomial,
  intR,
  isZeroR,
  mulR,
  parseRational,
  RATIONAL_ZERO,
  type Rational,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Finding a rule for a list of numbers.
 *
 * A finite list fits infinitely many rules, so this module is deliberately conservative:
 * it only reports a rule that has at least one term to spare — a rule with k unknowns is
 * fitted to the first terms and must then predict a term it was not fitted to. A quadratic
 * needs 4 terms, not 3; a Fibonacci-style recurrence needs 5. All arithmetic is exact
 * rational arithmetic, so 1/3, 2/3, 1 is arithmetic with d = 1/3, not 0.33333.
 */

export const MAX_TERMS = 24;
export const MAX_NEXT = 20;
export const MAX_TOKEN_DIGITS = 30;

export type RuleKind =
  | "arithmetic"
  | "geometric"
  | "polynomial"
  | "affine"
  | "recurrence";

export interface SequenceRule {
  kind: RuleKind;
  /** Degree of the polynomial (1 for arithmetic); 0 for the other kinds. */
  degree: number;
  /** Parameters as text, keyed by symbol: d, r, c, p, q, a1. */
  values: Record<string, string>;
  /** The nth-term formula, for example "aₙ = 3n + 2". */
  formula: string;
  /** The rule as a recurrence, where it has a natural one. */
  recurrence: string | null;
  /** The predicted terms after the list. */
  next: Rational[];
}

export interface SolveResult {
  rules: SequenceRule[];
  /** Row 0 is the terms; each later row is the differences of the one above. */
  differences: Rational[][];
}

/* ------------------------------------------------------------- reading */

export interface RationalList {
  values: Rational[];
  invalid: string[];
  tooMany: boolean;
  tooLong: boolean;
}

const SEMICOLON_SEPARATORS = /[\s;]+/;
const COMMA_SEPARATORS = /[\s,;]+/;
const DIGIT_RUN = /\d+/g;

/**
 * Numbers, decimals or fractions separated by spaces or commas — or, when the text has a
 * semicolon, by semicolons and spaces so that the comma can be a decimal mark.
 */
export function parseRationalList(raw: string): RationalList {
  const separators = raw.includes(";")
    ? SEMICOLON_SEPARATORS
    : COMMA_SEPARATORS;
  const result: RationalList = {
    values: [],
    invalid: [],
    tooMany: false,
    tooLong: false,
  };
  for (const token of raw.split(separators)) {
    if (token === "") {
      continue;
    }
    const longest = Math.max(
      0,
      ...(token.match(DIGIT_RUN) ?? []).map((run) => run.length)
    );
    if (longest > MAX_TOKEN_DIGITS) {
      result.tooLong = true;
      continue;
    }
    const value = parseRational(token);
    if (value === null) {
      result.invalid.push(token);
    } else {
      result.values.push(value);
    }
  }
  result.tooMany = result.values.length > MAX_TERMS;
  return result;
}

/* ---------------------------------------------------------- arithmetic */

/** Rows of successive differences, down to a single entry. */
export function differenceRows(terms: readonly Rational[]): Rational[][] {
  const rows: Rational[][] = [[...terms]];
  while ((rows.at(-1)?.length ?? 0) > 1) {
    const last = rows.at(-1) ?? [];
    rows.push(last.slice(1).map((value, index) => subR(value, last[index])));
  }
  return rows;
}

function allEqual(values: readonly Rational[]): boolean {
  return values.every((value) => equalsR(value, values[0]));
}

type Polynomial = Rational[];

function polyEvaluate(coefficients: Polynomial, x: Rational): Rational {
  let total = RATIONAL_ZERO;
  for (let power = coefficients.length - 1; power >= 0; power -= 1) {
    total = addR(mulR(total, x), coefficients[power]);
  }
  return total;
}

/** Multiply a polynomial (lowest power first) by (n − shift) ÷ divisor. */
function timesLinear(
  poly: Polynomial,
  shift: number,
  divisor: number
): Polynomial {
  const out: Polynomial = new Array(poly.length + 1).fill(RATIONAL_ZERO);
  for (const [power, coefficient] of poly.entries()) {
    out[power + 1] = addR(out[power + 1], coefficient);
    out[power] = subR(out[power], mulR(coefficient, intR(shift)));
  }
  return out.map((value) => divR(value, intR(divisor)));
}

/**
 * The polynomial through the terms (n = 1, 2, …) by Newton's forward differences:
 * aₙ = Σ Δᵏa₁ · C(n − 1, k), expanded into powers of n.
 */
export function newtonPolynomial(
  firsts: readonly Rational[],
  degree: number
): Polynomial {
  let basis: Polynomial = [intR(1)];
  let total: Polynomial = [RATIONAL_ZERO];
  for (let k = 0; k <= degree; k += 1) {
    if (k > 0) {
      basis = timesLinear(basis, k, k);
    }
    total = addPoly(
      total,
      basis.map((c) => mulR(c, firsts[k]))
    );
  }
  return total;
}

function addPoly(a: Polynomial, b: Polynomial): Polynomial {
  const size = Math.max(a.length, b.length);
  return Array.from({ length: size }, (_, i) =>
    addR(a[i] ?? RATIONAL_ZERO, b[i] ?? RATIONAL_ZERO)
  );
}

function variableFor(power: number): string {
  if (power === 0) {
    return "";
  }
  return power === 1 ? "n" : `n${toSuperscript(power)}`;
}

export function formatPolyInN(coefficients: Polynomial): string {
  return formatPolynomial(
    coefficients
      .map((coefficient, power) => ({
        coefficient,
        variable: variableFor(power),
      }))
      .reverse()
  );
}

function nextFromPolynomial(
  coefficients: Polynomial,
  have: number,
  count: number
): Rational[] {
  return Array.from({ length: count }, (_, i) =>
    polyEvaluate(coefficients, intR(have + 1 + i))
  );
}

function polynomialRule(
  terms: readonly Rational[],
  rows: readonly Rational[][],
  degree: number,
  count: number
): SequenceRule {
  const coefficients = newtonPolynomial(
    rows.slice(0, degree + 1).map((row) => row[0]),
    degree
  );
  const trimmed = coefficients.slice(0, degree + 1);
  const values: Record<string, string> = { a1: formatFraction(terms[0]) };
  if (degree === 1) {
    values.d = formatFraction(rows[1][0]);
  }
  return {
    kind: degree === 1 ? "arithmetic" : "polynomial",
    degree,
    values,
    formula: `aₙ = ${formatPolyInN(trimmed)}`,
    recurrence: degree === 1 ? `aₙ = aₙ₋₁ ${signed(rows[1][0])}` : null,
    next: nextFromPolynomial(trimmed, terms.length, count),
  };
}

function signed(value: Rational): string {
  const text = formatFraction(value);
  return text.startsWith("−") ? `− ${text.slice(1)}` : `+ ${text}`;
}

/** "expr", "−expr" or "c · expr" — a coefficient of 1 is left unwritten. */
function scaled(coefficient: Rational, expression: string): string {
  if (equalsR(coefficient, intR(1))) {
    return expression;
  }
  if (equalsR(coefficient, intR(-1))) {
    return `−${expression}`;
  }
  return `${bracket(coefficient)} · ${expression}`;
}

/** "+ expr", "− 2 · expr": a term to append after another. */
function signedTerm(coefficient: Rational, expression: string): string {
  const negative = coefficient.num < 0;
  const magnitude = negative
    ? { num: -coefficient.num, den: coefficient.den }
    : coefficient;
  return `${negative ? "−" : "+"} ${scaled(magnitude, expression)}`;
}

/** Wrap negatives and fractions so r^(n − 1) reads unambiguously. */
function bracket(value: Rational): string {
  const text = formatFraction(value);
  return text.startsWith("−") || text.includes("/") ? `(${text})` : text;
}

function findPolynomial(
  terms: readonly Rational[],
  rows: readonly Rational[][],
  count: number
): SequenceRule | null {
  // Degree d is confirmed when the d-th differences are constant over at least 2 entries.
  for (let degree = 1; degree < rows.length; degree += 1) {
    const row = rows[degree];
    if (row.length >= 2 && allEqual(row)) {
      return polynomialRule(terms, rows, degree, count);
    }
  }
  return null;
}

/* ------------------------------------------------------------ geometric */

function findGeometric(
  terms: readonly Rational[],
  count: number
): SequenceRule | null {
  if (terms.length < 3 || terms.some(isZeroR)) {
    return null;
  }
  const ratios = terms.slice(1).map((term, i) => divR(term, terms[i]));
  if (!allEqual(ratios) || equalsR(ratios[0], intR(1))) {
    return null;
  }
  const r = ratios[0];
  const next: Rational[] = [];
  let last = terms.at(-1) ?? terms[0];
  for (let i = 0; i < count; i += 1) {
    last = mulR(last, r);
    next.push(last);
  }
  return {
    kind: "geometric",
    degree: 0,
    values: { a1: formatFraction(terms[0]), r: formatFraction(r) },
    formula: `aₙ = ${scaled(terms[0], `${bracket(r)}^(n − 1)`)}`,
    recurrence: `aₙ = ${scaled(r, "aₙ₋₁")}`,
    next,
  };
}

/* ---------------------------------------------------------- recurrences */

function runRecurrence(
  terms: readonly Rational[],
  count: number,
  step: (previous: Rational, beforeThat: Rational) => Rational
): Rational[] {
  const out: Rational[] = [];
  let [beforeThat, previous] = terms.slice(-2);
  for (let i = 0; i < count; i += 1) {
    const value = step(previous, beforeThat);
    out.push(value);
    beforeThat = previous;
    previous = value;
  }
  return out;
}

function findAffine(
  terms: readonly Rational[],
  count: number
): SequenceRule | null {
  if (terms.length < 4) {
    return null;
  }
  const first = subR(terms[1], terms[0]);
  if (isZeroR(first)) {
    return null;
  }
  const r = divR(subR(terms[2], terms[1]), first);
  const c = subR(terms[1], mulR(r, terms[0]));
  const fits = terms
    .slice(1)
    .every((term, i) => equalsR(term, addR(mulR(r, terms[i]), c)));
  if (!fits || isZeroR(c) || isZeroR(r) || equalsR(r, intR(1))) {
    return null;
  }
  const shift = divR(c, subR(r, intR(1)));
  const lead = addR(terms[0], shift);
  const tail = isZeroR(shift) ? "" : ` ${signed(subR(RATIONAL_ZERO, shift))}`;
  return {
    kind: "affine",
    degree: 0,
    values: {
      r: formatFraction(r),
      c: formatFraction(c),
      a1: formatFraction(terms[0]),
    },
    formula: `aₙ = ${scaled(lead, `${bracket(r)}^(n − 1)`)}${tail}`,
    recurrence: `aₙ = ${scaled(r, "aₙ₋₁")} ${signed(c)}`,
    next: runRecurrence(terms, count, (previous) => addR(mulR(r, previous), c)),
  };
}

function findSecondOrder(
  terms: readonly Rational[],
  count: number
): SequenceRule | null {
  if (terms.length < 5) {
    return null;
  }
  const [a1, a2, a3, a4] = terms;
  const det = subR(mulR(a2, a2), mulR(a1, a3));
  if (isZeroR(det)) {
    return null;
  }
  const p = divR(subR(mulR(a3, a2), mulR(a1, a4)), det);
  const q = divR(subR(mulR(a2, a4), mulR(a3, a3)), det);
  const fits = terms
    .slice(2)
    .every((term, i) =>
      equalsR(term, addR(mulR(p, terms[i + 1]), mulR(q, terms[i])))
    );
  if (!fits) {
    return null;
  }
  return {
    kind: "recurrence",
    degree: 2,
    values: { p: formatFraction(p), q: formatFraction(q) },
    formula: "",
    recurrence: `aₙ = ${scaled(p, "aₙ₋₁")} ${signedTerm(q, "aₙ₋₂")}`,
    next: runRecurrence(terms, count, (previous, beforeThat) =>
      addR(mulR(p, previous), mulR(q, beforeThat))
    ),
  };
}

/* ---------------------------------------------------------------- solve */

/** Every rule that fits the terms with a term to spare, simplest first. */
export function solveSequence(
  terms: readonly Rational[],
  nextCount: number
): SolveResult {
  const differences = differenceRows(terms);
  const rules: SequenceRule[] = [];
  const closed = [
    findPolynomial(terms, differences, nextCount),
    findGeometric(terms, nextCount),
    findAffine(terms, nextCount),
  ];
  for (const rule of closed) {
    if (rule) {
      rules.push(rule);
    }
  }
  if (rules.length === 0) {
    const recurrence = findSecondOrder(terms, nextCount);
    if (recurrence) {
      rules.push(recurrence);
    }
  }
  return { rules, differences };
}

/** The smallest number of terms that could confirm each kind of rule. */
export const TERMS_NEEDED = {
  arithmetic: 3,
  geometric: 3,
  quadratic: 4,
  affine: 4,
  recurrence: 5,
} as const;
